import { useEffect, useRef, useState } from "react";

interface UseGenerationElapsedTimerOptions {
    /** Whether a (re)generation is currently running. The timer ticks only while true. */
    isRunning: boolean;
    /** Current message id. Switching messages resets the timer (two tickets can
     * generate at once). */
    messageId: string | null | undefined;
}

interface UseGenerationElapsedTimerReturn {
    /** Whole seconds since the current run started (0 when idle). */
    elapsedSeconds: number;
    /** True once the operator chose "keep waiting" past the overrun threshold;
     * consumers use it to hide the wait/restart prompt until a reappear threshold. */
    keepWaitingDismissed: boolean;
    /** Mark the overrun prompt dismissed ("Weiter warten"). */
    dismissKeepWaiting: () => void;
}

/**
 * Elapsed-seconds clock for a running answer (re)generation, shared by Neptune
 * and Venus so the re-baselining rules live in one place.
 *
 * The clock resets on exactly two events: a run starting (isRunning false ->
 * true) and a message switch. Nothing else re-baselines it -- in particular a
 * language pick does not, because it no longer replaces the running generation:
 * the run is the same run, and the time the operator has already waited is real
 * (spec rule 4.6).
 */
export function useGenerationElapsedTimer({
    isRunning,
    messageId,
}: UseGenerationElapsedTimerOptions): UseGenerationElapsedTimerReturn {
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [keepWaitingDismissed, setKeepWaitingDismissed] = useState(false);

    const activeRef = useRef(false);
    const msgRef = useRef(messageId);

    // Reset on message switch (render-time state adjustment -- two tickets can
    // be generating at once, so a switch must not show the other's elapsed time).
    const [syncedMessageId, setSyncedMessageId] = useState(messageId);
    if (syncedMessageId !== messageId) {
        setSyncedMessageId(messageId);
        setElapsedSeconds(0);
        setKeepWaitingDismissed(false);
    }

    useEffect(() => {
        if (msgRef.current !== messageId) {
            msgRef.current = messageId;
            activeRef.current = false;
        }
        if (!isRunning) {
            activeRef.current = false;
            return;
        }
        // Re-baseline on first activation only.
        if (!activeRef.current) {
            activeRef.current = true;
            setElapsedSeconds(0);
            setKeepWaitingDismissed(false);
        }
        const start = Date.now();
        const id = setInterval(() => {
            setElapsedSeconds((prev) => {
                const next = Math.floor((Date.now() - start) / 1000);
                return next > prev ? next : prev;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [isRunning, messageId]);

    return {
        elapsedSeconds,
        keepWaitingDismissed,
        dismissKeepWaiting: () => setKeepWaitingDismissed(true),
    };
}
